import { NavLink, useLocation } from 'react-router-dom';
import { useState } from 'react';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PetsIcon from '@mui/icons-material/Pets';
import GroupsIcon from '@mui/icons-material/Groups';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import StarsOutlinedIcon from '@mui/icons-material/StarsOutlined';

const EXPANDED_W = 260;
const COLLAPSED_W = 76;

const navItems = [
  { to: '/riders',           label: 'Riders',          icon: PersonOutlineIcon,    color: '#60a5fa' },
  { to: '/horse',            label: 'Horse',            icon: PetsIcon,             color: '#34d399' },
  { to: '/clubs',            label: 'Clubs',            icon: GroupsIcon,           color: '#f472b6' },
  { to: '/calendar',         label: 'Calendar',         icon: CalendarMonthIcon,    color: '#fb923c' },
  { to: '/users',            label: 'Users',            icon: PeopleOutlineIcon,    color: '#a78bfa' },
  { to: '/dashboard',        label: 'Dashboard',        icon: DashboardOutlinedIcon,color: '#38bdf8' },
  { to: '/marketplace',      label: 'Marketplace',      icon: StorefrontOutlinedIcon,color:'#fbbf24' },
  { to: '/analytics',        label: 'Analytics',        icon: AnalyticsOutlinedIcon,color: '#4ade80' },
  { to: '/transactions',     label: 'Transactions',     icon: PaymentOutlinedIcon,  color: '#f87171' },
  { to: '/video-analytics',  label: 'Video Analytics',  icon: VideocamOutlinedIcon, color: '#c084fc' },
  { to: '/sessional-points', label: 'Sessional Points', icon: StarsOutlinedIcon,    color: '#facc15' },
];

function pathMatches(pathname, to) {
  if (to === '/riders' && pathname.startsWith('/riders')) return true;
  if (to === '/horse' && pathname.startsWith('/horse')) return true;
  if (to === '/clubs' && pathname.startsWith('/clubs')) return true;
  if (to === '/calendar' && pathname.startsWith('/calendar')) return true;
  if (to === '/users' && pathname.startsWith('/users')) return true;
  if (to === '/dashboard' && pathname.startsWith('/dashboard')) return true;
  if (to === '/marketplace' && pathname.startsWith('/marketplace')) return true;
  if (to === '/analytics' && pathname.startsWith('/analytics')) return true;
  if (to === '/transactions' && pathname.startsWith('/transactions')) return true;
  if (to === '/video-analytics' && pathname.startsWith('/video-analytics')) return true;
  if (to === '/sessional-points' && pathname.startsWith('/sessional-points')) return true;
  return pathname === to;
}

const SideNav = () => {
  const [open, setOpen] = useState(true);
  const location = useLocation();
  const width = open ? EXPANDED_W : COLLAPSED_W;

  return (
    <Box
      component="aside"
      sx={{
        width,
        flexShrink: 0,
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 60%, #0f172a 100%)',
        color: 'rgba(255,255,255,0.92)',
        transition: 'width 0.22s ease',
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid rgba(255,255,255,0.04)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(99,102,241,0.8), transparent)',
        },
      }}
    >
      {/* Decorative glow blob */}
      <Box
        sx={{
          position: 'absolute',
          top: 60,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 160,
          height: 160,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* Logo area */}
      <Box
        sx={{
          px: open ? 2.5 : 1,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'flex-start' : 'center',
          minHeight: 72,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: open ? 20 : 8,
            right: open ? 20 : 8,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(139,92,246,0.5), transparent)',
          },
        }}
      >
        <img
          src="https://alumnikenverse.blob.core.windows.net/images/logo1.svg"
          alt="Equios"
          style={{
            height: open ? 36 : 28,
            width: 'auto',
            objectFit: 'contain',
            filter: 'brightness(0) invert(1)',
            opacity: 0.95,
          }}
        />
        {open && (
          <Box sx={{ ml: 1.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                letterSpacing: '0.08em',
                fontSize: '0.65rem',
                textTransform: 'uppercase',
                background: 'linear-gradient(135deg, #a78bfa, #60a5fa)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Admin
            </Typography>
          </Box>
        )}
      </Box>

      {/* Collapse toggle */}
      <IconButton
        onClick={() => setOpen((v) => !v)}
        size="small"
        sx={{
          position: 'absolute',
          right: -14,
          top: 24,
          zIndex: 2,
          width: 28,
          height: 28,
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          color: '#fff',
          border: '2px solid rgba(255,255,255,0.15)',
          boxShadow: '0 4px 12px rgba(99,102,241,0.4)',
          '&:hover': {
            background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
            boxShadow: '0 4px 16px rgba(99,102,241,0.6)',
          },
        }}
        aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
      >
        {open ? <ChevronLeftIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
      </IconButton>

      {/* Nav items */}
      <List sx={{ px: 1.5, py: 2, flex: 1, overflow: 'auto' }}>
        {navItems.map(({ to, label, icon: Icon, color }) => {
          const selected = pathMatches(location.pathname, to);
          const button = (
            <ListItemButton
              component={NavLink}
              to={to}
              selected={selected}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                py: 1,
                px: open ? 1.5 : 1,
                justifyContent: open ? 'flex-start' : 'center',
                color: selected ? '#fff' : 'rgba(255,255,255,0.6)',
                position: 'relative',
                overflow: 'hidden',
                background: selected
                  ? `linear-gradient(135deg, ${color}22 0%, ${color}15 100%)`
                  : 'transparent',
                border: selected ? `1px solid ${color}33` : '1px solid transparent',
                '&::before': selected
                  ? {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '20%',
                      bottom: '20%',
                      width: 3,
                      borderRadius: '0 3px 3px 0',
                      background: color,
                      boxShadow: `0 0 10px ${color}`,
                    }
                  : {},
                '&.Mui-selected': {
                  bgcolor: 'transparent',
                  color: '#fff',
                  '&:hover': { bgcolor: `${color}18` },
                },
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.07)',
                  color: '#fff',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: open ? 36 : 0,
                  color: selected ? color : 'inherit',
                  justifyContent: 'center',
                  filter: selected ? `drop-shadow(0 0 6px ${color})` : 'none',
                  transition: 'filter 0.2s',
                }}
              >
                <Icon fontSize="small" />
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: selected ? 700 : 500,
                    fontSize: '0.875rem',
                    letterSpacing: selected ? '0.01em' : 0,
                  }}
                />
              )}
            </ListItemButton>
          );

          return (
            <ListItem key={to} disablePadding sx={{ display: 'block' }}>
              {open ? (
                button
              ) : (
                <Tooltip title={label} placement="right" arrow>
                  <Box component="span" sx={{ display: 'block' }}>{button}</Box>
                </Tooltip>
              )}
            </ListItem>
          );
        })}
      </List>

      {/* Bottom label */}
      {open && (
        <Box sx={{ px: 2.5, py: 2, opacity: 0.35 }}>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Equios Admin v1.0
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SideNav;
